import React from 'react';
import styled from 'styled-components';

import { media } from '../../core/breakpoints';
import { convertPxToRem } from '../../core/utils';

type AlignGridItems = 'baseline' | 'center' | 'end' | 'start' | 'stretch';

interface GridProps {
  alignItems?: AlignGridItems;
  children: React.ReactNode;
}

interface FullWidth {
  isFullWidth: boolean;
}

interface Columns {
  columns: {
    startingColumn: number;
    numberOfColumnsToSpan: number;
  };
}

interface AutoSpan {
  autoSpanNumberOfColumns: number;
}

interface GridColumnProps {
  breakpoint?: string;
  children: React.ReactNode;
  isFirstOnMobile?: boolean;
  layout: FullWidth | Columns | AutoSpan;
}

interface SvgContainerStylesProps {
  widthInPx: number;
}

interface SvgContainerProps {
  children: React.ReactNode;
  widthInPx: number;
}

export const Row = styled.div`
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
`;

export const Column = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 10px;
`;

export const Grid = styled.div<GridProps>`
  display: grid;
  align-items: ${({ alignItems }) => alignItems || 'center'};
  grid-gap: 1.5rem;
  grid-template-columns: repeat(12, 1fr);
  padding: 0 4.5rem;

  ${media.tabletMedium} {
    padding: 0 1.5rem;
  }
`;

const determineGridColumnStyles = (layoutObj: any) => {
  if (layoutObj.hasOwnProperty('isFullWidth')) {
    return '1 / -1;';
  }

  if (layoutObj.hasOwnProperty('columns')) {
    return `${layoutObj.columns.startingColumn} / span ${layoutObj.columns.numberOfColumnsToSpan}`;
  }

  if (layoutObj.hasOwnProperty('autoSpanNumberOfColumns')) {
    return `span ${layoutObj.autoSpanNumberOfColumns}`;
  }

  return '1 / -1;';
};

export const GridColumn = styled.div<GridColumnProps>`
  grid-column: ${({ layout }) => determineGridColumnStyles(layout)};

  ${({ breakpoint }) => (breakpoint ? `@media ${breakpoint}` : media.tabletMedium)} {
    grid-column: 1 / -1;
    order: ${({ isFirstOnMobile }) => (isFirstOnMobile ? -1 : 'initial')};
  }
`;

export const SvgContainerStyles = styled.div<SvgContainerStylesProps>`
  svg {
    width: ${({ widthInPx }) => `${convertPxToRem(widthInPx)}`};
  }
`;

export const SvgContainer = ({ children, widthInPx }: SvgContainerProps) => (
  <SvgContainerStyles widthInPx={widthInPx}>{children}</SvgContainerStyles>
);
